"use client";

import { useMemo, useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Microphone } from "@/types";

gsap.registerPlugin(ScrollTrigger);

interface AlphabetNavProps {
  microphones: Microphone[];
  onLetterClick: (letter: string) => void;
}

export default function AlphabetNav({
  microphones,
  onLetterClick,
}: AlphabetNavProps) {
  const navRef = useRef<HTMLDivElement>(null);
  const [currentLetter, setCurrentLetter] = useState<string>("");

  // Generate alphabet array
  const alphabet = Array.from({ length: 26 }, (_, i) =>
    String.fromCharCode(65 + i)
  );

  // Find which letters have microphones
  const availableLetters = useMemo(() => {
    const letters = new Set();
    microphones.forEach((mic) => {
      const firstLetter = `${mic.manufacturer} ${mic.model}`
        .charAt(0)
        .toUpperCase();
      if (firstLetter >= "A" && firstLetter <= "Z") {
        letters.add(firstLetter);
      }
    });
    return letters;
  }, [microphones]);

  useEffect(() => {
    // Only run on client-side
    if (typeof window === 'undefined' || !navRef.current) return;

    const isMobile = window.innerWidth < 768;

    const setupScrollTrigger = () => {
      // Kill any existing triggers first
      ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger.trigger === navRef.current) {
          trigger.kill();
        }
      });

      if (!navRef.current) return;

      const navHeight = navRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;

      // Check if navigation fits in viewport when pinned at 20px
      const navFitsInViewport = navHeight + 20 + 20 <= viewportHeight; // 20px top + 20px buffer bottom

      if (navFitsInViewport && !isMobile) {
        // Normal behavior: pin at 20px from top (desktop only)
        ScrollTrigger.create({
          trigger: navRef.current,
          start: "top 20px",
          endTrigger: ".collection-container",
          end: "bottom bottom",
          pin: true,
          pinSpacing: false,
        });
      } else if (navFitsInViewport && isMobile) {
        // Mobile with short nav: use simple pin (same as desktop)
        ScrollTrigger.create({
          trigger: navRef.current,
          start: "top 80px", // top-20 = 80px on mobile
          endTrigger: ".collection-container",
          end: "bottom bottom",
          pin: true,
          pinSpacing: false,
        });
      } else {
        // For tall navigation: "Smart Sticky" behavior
        // A scrolls away -> Z locks to bottom -> Reverse direction -> A comes back

        let currentY = 0;
        let lastScrollY: number | null = null; // Initialize on first update for consistency
        let scrollDirection: 'down' | 'up' = 'down'; // Track direction with hysteresis
        let pendingUpdate = false;

        ScrollTrigger.create({
          trigger: ".collection-container",
          start: "top top",
          end: "bottom top",  // Keep active until container is fully scrolled out of view
          onUpdate: (self) => {
            const currentScroll = self.scroll();

            // Initialize on first call to ensure consistent coordinate system
            if (lastScrollY === null) {
              lastScrollY = currentScroll;
              return;
            }

            const diff = currentScroll - lastScrollY;
            lastScrollY = currentScroll;

            // Higher threshold on mobile to reduce jitter from touch momentum
            const DIRECTION_THRESHOLD = isMobile ? 8 : 3;
            if (diff > DIRECTION_THRESHOLD) {
              scrollDirection = 'down';
            } else if (diff < -DIRECTION_THRESHOLD) {
              scrollDirection = 'up';
            }
            // If within threshold, keep current direction (hysteresis)

            const nav = navRef.current;
            if (!nav) return;

            const container = document.querySelector(".collection-container") as HTMLElement;
            if (!container) return;

            const viewportHeight = window.innerHeight;
            const navHeight = nav.offsetHeight;
            const containerHeight = container.offsetHeight;
            const maxTravel = containerHeight - navHeight - 120; // 120px buffer for footer/offsets

            const containerRect = container.getBoundingClientRect();
            const containerTop = containerRect.top;

            // 96px is top-24 (6rem)
            const headerOffset = 100; // Header height
            const bottomBuffer = 20;  // Bottom padding

            if (scrollDirection === 'down') {
               // Pin nav to BOTTOM of viewport
               // We want: navBottom = viewportHeight - bottomBuffer
               // navBottom = containerTop + 96 + currentY + navHeight
               // So: currentY = viewportHeight - bottomBuffer - containerTop - 96 - navHeight
               const targetY = viewportHeight - bottomBuffer - containerTop - 96 - navHeight;

               // Only move down, never up when scrolling down
               if (targetY > currentY) {
                   currentY = targetY;
               }
            } else { // scrollDirection === 'up'
               // Pin nav to TOP of viewport (below header)
               // We want: navTop = headerOffset
               // navTop = containerTop + 96 + currentY
               // So: currentY = headerOffset - containerTop - 96
               const targetY = headerOffset - containerTop - 96;

               // Only move up, never down when scrolling up
               if (targetY < currentY) {
                   currentY = targetY;
               }
            }

            // Clamp Y to container bounds (absolute safety)
            currentY = Math.max(0, Math.min(currentY, maxTravel));

            if (isMobile) {
              // Batch updates via RAF on mobile to reduce jitter from touch momentum
              if (!pendingUpdate) {
                pendingUpdate = true;
                requestAnimationFrame(() => {
                  gsap.set(nav, { y: currentY, force3D: true });
                  pendingUpdate = false;
                });
              }
            } else {
              // Desktop: immediate update for smooth response
              gsap.set(nav, { y: currentY, force3D: true });
            }
          }
        });
      }
    };

    // Simple scroll handler for current position detection  
    const handleScroll = () => {
      // Find which microphone section is currently in view
      const micElements = document.querySelectorAll('[id^="mic-"]');
      const viewportCenter = window.innerHeight / 2;

      let closestLetter = "";
      let closestDistance = Infinity;

      micElements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const elementCenter = rect.top + rect.height / 2;
        const distance = Math.abs(elementCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          const idParts = el.id.split("-");
          if (idParts.length > 1) {
            closestLetter = idParts[1];
          }
        }
      });

      setCurrentLetter(closestLetter);
    };

    // Add scroll listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Setup with a small delay to ensure DOM is ready
    setTimeout(setupScrollTrigger, 100);

    // Also refresh ScrollTrigger when returning to the page
    ScrollTrigger.refresh();

    // Store current ref for cleanup
    const currentNav = navRef.current;
    
    // Cleanup
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener("scroll", handleScroll);
        ScrollTrigger.getAll().forEach((trigger) => {
          if (trigger.trigger === currentNav) {
            trigger.kill();
          }
        });
      }
    };
  }, [availableLetters]);

  return (
    <div ref={navRef} className="absolute right-3 md:right-7 top-20 md:top-24 z-50 will-change-transform">
      <div className="flex flex-col gap-1 rounded-lg p-2">
        {alphabet.map((letter) => {
          const hasItems = availableLetters.has(letter);
          const isCurrent = currentLetter === letter;
          
          // Debug logging (development only)
          if (process.env.NODE_ENV === 'development' && isCurrent) {
            console.log(`Current letter: ${letter}`);
          }
          
          return (
            <button
              key={letter}
              onClick={() => hasItems && onLetterClick(letter)}
              disabled={!hasItems}
              className={`
                w-8 h-8 rounded-lg transition-all duration-200 cursor-pointer flex items-center justify-center
                ${isCurrent
                  ? "text-lg font-black scale-125 text-white"
                  : "text-xs font-semibold text-white"
                }
                ${
                  hasItems
                    ? "hover:bg-white/20 hover:scale-110"
                    : "text-white/30 cursor-not-allowed"
                }
              `}
              title={
                hasItems
                  ? `Jump to ${letter}`
                  : `No microphones starting with ${letter}`
              }
            >
              {letter}
            </button>
          );
        })}
      </div>
    </div>
  );
}
